import React, { useContext, useState } from 'react';
import { InputField, RadioGroupField } from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import {
  invalidCitizenshipValue,
  mapCitizenshipToSsnLabel,
  maskSsnForDisplay
} from './util';
import {
  formatSsnForDisplay,
  handleInputFormat,
  isTaxable
} from '../../flows/shared/utils';
import {
  firstnameValidation,
  middlenameValidation,
  lastnameValidation,
  restrictedFirstNameValidation,
  restrictedLastNameValidation,
  restrictedMiddleNameValidation
} from '../../flows/shared/validations';
import styles from './style.css';
import { withDefault } from '../../core/util';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';

export const CitizenshipSection = ({
  authenticated,
  citizenship,
  citizenShipPresent,
  handleCitizenshipBtnClick
}) => {
  // user is authenticated and has citizenship, bail out
  if (authenticated && citizenShipPresent) return null;

  // otherwise render citizenship field
  return (
    <div className="citizenship-input">
      <RadioGroupField
        title="Citizenship"
        onChange={handleCitizenshipBtnClick}
        options={[
          {
            title: 'U.S. Citizen',
            value: 'US_CITIZEN',
            defaultChecked:
              citizenship === 'US_CITIZEN' ||
              invalidCitizenshipValue(citizenship)
          },
          {
            title: 'U.S. Resident Alien',
            value: 'RESIDENT_ALIEN',
            defaultChecked: citizenship === 'RESIDENT_ALIEN'
          }
        ]}
        name="citizenship"
      />
      <style jsx>{styles}</style>
    </div>
  );
};

const PersonalInformationSection = () => {
  const { formProps, appState } = useContext(FlowContext);
  const {
    store: {
      enabled,
      app,
      accountType,
      authenticated,
      loadedData: { citizenShipPresent }
    }
  } = appState;
  const {
    customer: {
      name: { firstName, lastName, middleName },
      dob,
      taxID,
      citizenship
    }
  } = app;

  const [ssnLabel, setSsnLabel] = useState(
    mapCitizenshipToSsnLabel(citizenship)
  );
  const [dobValue, setDobValue] = useState('');
  const [ssnValue, setSSNValue] = useState('');

  const handleCitizenshipBtnClick = ({ target: { value } }) =>
    setSsnLabel(mapCitizenshipToSsnLabel(value));

  const isTaxableFlow = isTaxable(accountType);
  const firstNameValidation = enabled
    ? firstnameValidation
    : restrictedFirstNameValidation;
  const middleNameValidation = enabled
    ? middlenameValidation
    : restrictedMiddleNameValidation;
  const lastNameValidation = enabled
    ? lastnameValidation
    : restrictedLastNameValidation;

  return (
    <>
      <div className="name-fields">
        <div className="first-name">
          <InputField
            type="text"
            title="First Name"
            {...formProps.firstName}
            data-name={generateGeneralTestID(
              'PersonalInformation',
              'AccountOwnerFirstName'
            )}
            disabled={enabled}
            defaultValue={firstName}
            validation={firstNameValidation}
            className="blocked_data"
          />
        </div>

        <div className="middle-name">
          <InputField
            type="text"
            title="Middle Name"
            {...formProps.middleName}
            data-name={generateGeneralTestID(
              'PersonalInformation',
              'AccountOwnerMiddleName'
            )}
            disabled={enabled}
            defaultValue={middleName}
            validation={middleNameValidation}
            className="blocked_data"
          />
        </div>
        <div className="last-name">
          <InputField
            type="text"
            title="Last Name"
            {...formProps.lastName}
            data-name={generateGeneralTestID(
              'PersonalInformation',
              'AccountOwnerLastName'
            )}
            disabled={enabled}
            defaultValue={lastName}
            validation={lastNameValidation}
            className="blocked_data"
          />
        </div>
      </div>
      <div className="personal-info-fields">
        <div className="dob-input">
          <InputField
            style={{ paddingRight: '8px' }}
            type="text"
            onChange={evt => handleInputFormat(evt, 'DATE', setDobValue)}
            placeholder="mm/dd/yyyy"
            title="Date of Birth"
            aria-describedby="hint"
            maxLength="10"
            {...formProps.dob}
            data-name={generateGeneralTestID(
              'PersonalInformation',
              'AccountOwnerBirthDate'
            )}
            disabled={(enabled || !isTaxableFlow) && dob}
            value={withDefault(dobValue, dob)}
            className="blocked_data"
          />
          <dd id="hint" style={{ display: 'none' }}>
            This is a required field. Please enter a two digit month, a two
            digit day and a four digit year for the Date of Birth value.
          </dd>
        </div>
        <div className="ssn-input">
          <InputField
            title={ssnLabel}
            placeholder="000-00-0000"
            maxLength="11"
            onChange={evt => handleInputFormat(evt, 'SSN', setSSNValue)}
            {...formProps.ssn}
            data-name={generateGeneralTestID(
              'PersonalInformation',
              'AccountOwnerTaxId'
            )}
            disabled={enabled}
            value={
              enabled
                ? maskSsnForDisplay(taxID)
                : withDefault(ssnValue, formatSsnForDisplay(taxID))
            }
            className="blocked_data"
          />
        </div>
        <CitizenshipSection
          authenticated={authenticated}
          citizenship={citizenship}
          citizenShipPresent={citizenShipPresent}
          handleCitizenshipBtnClick={handleCitizenshipBtnClick}
          dataNamePrefix="PersonalInformation"
        />
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default PersonalInformationSection;
